import React, { useState, useEffect } from 'react';
import Header from './Header.jsx';
import ChatWindow from './ChatWindow.jsx';
import MiniButton from './MiniButton.jsx';
import { Button } from './ui/button.jsx';
import { Trash2, X, Maximize2, Minimize2 } from 'lucide-react';
import { LanguageProvider } from '../contexts/LanguageContext';
import { ChatProvider, useChat } from '../contexts/ChatContext';
import { cn } from '../lib/utils';
import { useIsMobile } from '../hooks/use-mobile';

const AIAssistant = () => {
  const [isMiniMode, setIsMiniMode] = useState(false); // default to hidden
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      setIsMiniMode(false);
      setIsFullscreen(false);
    }
  }, [isMobile]);

  const toggleMiniMode = () => {
    setIsMiniMode(prev => !prev);
    setIsMinimized(false);
    setIsFullscreen(false);
  };

  const closeMiniMode = () => {
    setIsMiniMode(false);
    setIsMinimized(false);
    setIsFullscreen(false);
  };

  const toggleMinimized = () => {
    setIsMinimized(prev => !prev);
    setIsFullscreen(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
    setIsMinimized(false);
    setIsMiniMode(true);
  };

  return (
    <LanguageProvider>
      <ChatProvider>
        <AIAssistantInterface
          isMiniMode={isMiniMode}
          toggleMiniMode={toggleMiniMode}
          closeMiniMode={closeMiniMode}
          isMinimized={isMinimized}
          toggleMinimized={toggleMinimized}
          isFullscreen={isFullscreen}
          toggleFullscreen={toggleFullscreen}
        />
      </ChatProvider>
    </LanguageProvider>
  );
};

const AIAssistantInterface = ({
  isMiniMode,
  toggleMiniMode,
  closeMiniMode,
  isMinimized,
  toggleMinimized,
  isFullscreen,
  toggleFullscreen
}) => {
  const { clearMessages } = useChat();
  const isMobile = useIsMobile();

  // Only show the floating widget button when chat is closed
  return (
    <>
      {/* Floating widget button (only when chat is closed) */}
      {!isMiniMode && (
        <div className="fixed bottom-4 right-4 z-50">
          <MiniButton
            isMiniMode={isMiniMode}
            toggleMiniMode={toggleMiniMode}
            isMinimized={isMinimized}
            toggleMinimized={toggleMinimized}
          />
        </div>
      )}
      {/* Chat window overlay (only when mini mode is open) */}
      {isMiniMode && (
        <div className={
          isFullscreen
            ? 'fixed inset-0 w-screen h-screen z-50'
            : isMinimized
              ? 'fixed bottom-0 right-0 w-0 h-0 opacity-0 pointer-events-none transform translate-y-full z-50'
              : 'fixed bottom-4 right-4 w-[95vw] xs:w-[85vw] sm:w-[400px] md:w-[380px] h-[70vh] sm:h-[500px] max-h-[600px] shadow-lg rounded-lg overflow-hidden z-50 animate-fade-in pointer-events-auto'
        } style={{ pointerEvents: (!isMinimized || isFullscreen) ? 'auto' : 'none' }}>
          <div className={cn(
            'flex flex-col h-full border',
            isFullscreen ? 'rounded-none' : 'rounded-lg',
            'shadow-lg transition-all duration-300',
            'bg-white dark:bg-gray-900'
          )}>
            {/* Header with close (X) button */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-800">
              <span className="font-bold text-lg">Grocery Genie</span>
              <div className="flex gap-2 items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={clearMessages}
                  className="h-8 w-8 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-muted/80 transition-all duration-200"
                  title="Clear messages"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleFullscreen}
                  className="h-8 w-8 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-muted/80 transition-all duration-200"
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeMiniMode}
                  className="h-8 w-8 bg-white/80 dark:bg-gray-800/80 hover:bg-red-100 dark:hover:bg-red-900 transition-all duration-200"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex-1 flex flex-col overflow-hidden relative">
              <ChatWindow isMiniMode={isMiniMode || isFullscreen} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant; 